"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyFlavorOverrides = applyFlavorOverrides;
const Utils_1 = require("./Utils");
/* ── authored values (ORDER2_FLAVOR.md §3, source-fidelity pass 2026-07-24) ── */
/**
 * Showcase slot = licensed streaming service chips ONLY (the source cards carry
 * no includes bullets — the old `.sp-includes` <ul> was flagged as extra content
 * and removed). Chips sit directly under the gold ribbon.
 *
 * The chips carry the brands' LOGOS (2026-09-21): each `.sp-svc` wraps an <img>
 * of `/img/streaming/<class>.svg`, and the wordmark text moves to the `alt` — so
 * a blocked or 404'd asset degrades to exactly the text chips this row used to
 * render (`.sp-svc` keeps its per-brand text colour for that case). Optical
 * heights per brand live in _step2.scss; asset provenance in
 * src/public/img/streaming/README.md.
 */
const SVC_NAMES = {
    youtubetv: 'YouTube TV',
    netflix: 'NETFLIX',
    hbomax: 'HBO Max',
    disney: 'Disney+',
    hulu: 'hulu'
};
/**
 * Which services a chip row names, per MyBundle reward level.
 *
 * ⚠️ INCOMPLETE — every level currently falls through to `DEFAULT_SVCS`, i.e. the
 * five marks the design has always shown. The levels are real and they do NOT
 * carry the same catalog (Basic ≠ Premium), so a row that names a service the
 * customer's level cannot redeem is a false promise on the card. Fill this table
 * from MyBundle's reward catalog — one entry per level, and the row then follows
 * whichever addon the offer actually carries, with no other change.
 *
 * Reward levels ↔ ids, from the portal (`MyBundleController`, the item_price_id
 * switch + MBTV_VALID_REWARD_IDS): lite 678, basic 582, standard 583, premium
 * 584, and the 2026 term SKUs 2mo 782, 3mo 781, 4mo 783. `rewardLevel()` below
 * reads the level off the addon's item id, so keys here are those level names.
 */
const LEVEL_SVCS = {
// lite:     [...],
// basic:    [...],
// standard: [...],
// premium:  [...],
// '2mo': [...], '3mo': [...], '4mo': [...]
};
const DEFAULT_SVCS = ['youtubetv', 'netflix', 'hbomax', 'disney', 'hulu'];
/**
 * The reward level named by a MyBundle addon's Chargebee item id — the key into
 * LEVEL_SVCS. `MyBundleTV-Premium` → 'premium'; `MyBundleTV-2026-3MO` → '3mo';
 * anything else (a SKU naming scheme we have not seen) → null, which takes the
 * default row.
 */
function rewardLevel(sku) {
    const named = sku.match(/\b(lite|basic|standard|premium)\b/i);
    if (named) {
        return named[1].toLowerCase();
    }
    const term = sku.match(/(\d+)\s*-?\s*MO/i);
    return term ? `${term[1]}mo` : null;
}
const SVC_CHIP = (cls) => `<span class="sp-svc ${cls}"><img src="${(0, Utils_1.assetUrl)(`/img/streaming/${cls}.svg`)}" alt="${SVC_NAMES[cls]}"></span>`;
const showcaseSvcs = (sku) => {
    const level = rewardLevel(sku);
    const svcs = (level && LEVEL_SVCS[level]) || DEFAULT_SVCS;
    return `<div class="sp-promo-svcs">${svcs.map(SVC_CHIP).join('')}</div>`;
};
/** page-level plan-head — forced over whatever the portal sends (hidden in customerHub embeds) */
// h2, not h1 — the page's single h1 is the address-gate hero (step-1.ejs). #offers-header
// styles h1 and h2 the same, so this is a tag swap only. See the SEO handoff 2026-09-17, §5.
const OFFERS_HEADER = `<h2>Pick a plan</h2><p>100% fiber with no data caps and no contract &mdash; pick the plan that fits your needs.</p>`;
/**
 * Per-plan overrides, keyed by the Chargebee item id (= `offer.title`).
 * - `subtitle` uses the source's speed voice: `Gig`, not `Gbps` (1 Gig / 5 Gig /
 *   10 Gig; 250 Mbps stays). The client splits on the space (number + unit).
 * - `corner: 'Most popular'` on 5-Gig ONLY: mirrors the source's client-side
 *   demo badge on Orbit (buy.js demo-badges the popular tier; there is no
 *   server flavor for it yet — this is the transitional stand-in).
 * - the streaming ribbon (`topText`) and the service chips (`showcase`) are NOT
 *   authored here: they are derived per offer from its MyBundle addon, so a plan
 *   the catalog never attached the credit to cannot promise it. See
 *   streamingFlavor() — it also writes '' on both keys when there is no addon,
 *   which is what stops the portal's old-design values (the Price-Lock <img> in
 *   topText) from leaking through `Object.assign`.
 */
const PLAN_OVERRIDES = {
    '250-Mb-2025-Regular': {
        title: 'Liftoff',
        subtitle: '250 Mbps',
        description: 'Best for casual browsing, email, and online shopping',
        corner: '',
        image: ''
    },
    '1-Gig-2025-Regular': {
        title: 'Ascent',
        subtitle: '1 Gig',
        description: 'Great for streaming, video calls, and multiple devices',
        corner: '',
        image: ''
    },
    '5-Gig-2025-Regular': {
        title: 'Orbit',
        subtitle: '5 Gig',
        description: 'Most popular for heavy streamers, gamers, and smart homes',
        corner: 'Most popular',
        image: ''
    },
    '10-Gig-2025-Regular': {
        title: 'Warp Speed',
        subtitle: '10 Gig',
        description: 'Perfect for power users, content creators, and future-proofing',
        corner: '',
        image: ''
    }
};
/**
 * The streaming-credit slots for one offer — ribbon + service-logo chips, or
 * both blank.
 *
 * Gate: the offer's own addons must carry a FREE `type: 'MYBUNDLE'` item. No
 * addon (an SA/offer group the MyBundle SKUs are disqualified for, a plan the
 * campaign never attached them to, a paid variant) means no promise on the card
 * — which keeps the plan card, the Extras MyBundle line and the Details cart
 * consistent, since those already render off the same structured addon.
 *
 * The month count comes from the addon SKU (`MyBundleTV-2026-3MO`), not from the
 * plan: it is the credit's own term, and an unparseable SKU falls back to
 * count-neutral copy rather than inventing a number. The same SKU picks the chip
 * row, since reward levels do not all carry the same services (LEVEL_SVCS).
 */
function streamingFlavor(offer) {
    const myBundle = offer.addons?.find(a => a.type === 'MYBUNDLE' && (a.price ?? 0) === 0);
    if (!myBundle) {
        return { topText: '', showcase: '' };
    }
    const months = myBundle.title.match(/(\d+)\s*-?\s*MO/i);
    const amount = months
        ? `<b>${months[1]} months</b> of free streaming`
        : `<b>Free streaming</b> with MyBundle`;
    return {
        topText: `<span class="sp-promo-kick">Limited time</span><span class="sp-promo-amt">${amount}</span>`,
        // the chip row follows the reward level of THIS offer's addon (see LEVEL_SVCS)
        showcase: showcaseSvcs(myBundle.title)
    };
}
/**
 * Per-addon overrides, keyed by the Chargebee addon item id (= `addon.title`,
 * verified against the real dev-portal payload). The Total-Connect trio's
 * names/descriptions are hardcoded in step-extras.ejs — only the PRICE text
 * leaks from flavor (`button` feeds ExtrasStep.addonActionText, which renders
 * `+ $10/mo` on the trio card). `title` is also overridden because the Details
 * cart line renders `${flavor.title} @ $N/mo.` as HTML — the portal's old
 * TC-MAX title carries a `<span class='text-lime'>` that must not leak there.
 * Any Price-Lock addon is intentionally ABSENT — it passes through untouched.
 */
/** trio feature-list items (flavor.topText on the TC tiers) — <li> anatomy must match
 *  the .svc-feats fallback markup in step-extras.ejs (check svg + span) */
const FEAT = (html) => `<li><svg class="fa" viewBox="0 0 640 640"><use xlink:href="#check" /></svg><span>${html}</span></li>`;
const EERO = `<img src="${(0, Utils_1.assetUrl)('/img/eero-logo-crop.png')}" class="eero-logo" alt="eero">`;
const FEES = `Waived equipment and service <a href="https://wire3.com/support-policy/#fee-schedule" target="_blank">fees<em>*</em></a>`;
const ADDON_OVERRIDES = {
    // Mapping on the trio cards (ExtrasStep.buildTC): title → .svc-name,
    // bottomText → .svc-desc, subtitle → .svc-value ("$897 value"), topText →
    // .svc-feats <li>s, button → the price pill. Basic Connect has no catalog
    // addon (it's the absence of Total Connect) — its card copy is the static
    // EJS fallback by design.
    'Total-Connect': {
        title: 'Total Connect',
        subtitle: '$897 value',
        bottomText: 'Keep the entire family connected with whole home Wi‑Fi and dedicated support.',
        topText: FEAT(`${EERO} Pro 7 router + extender (with setup)`)
            + FEAT('Whole-home Wi‑Fi coverage')
            + FEAT('In-home support to connect Wi‑Fi devices')
            + FEAT(FEES),
        button: '$10/mo'
    },
    'Total-Connect-Max': {
        title: 'Total Connect MAX',
        subtitle: '$1,257 value',
        bottomText: 'End-to-end coverage for large homes and tech enthusiasts who want maximum performance with premium equipment and proactive monitoring.',
        topText: FEAT(`${EERO} <strong>Max 7</strong> routers (with setup)`)
            + FEAT('Whole-home Wi‑Fi coverage')
            + FEAT('In-home support to connect Wi‑Fi devices')
            + FEAT(FEES)
            + FEAT('Complimentary <a href="https://eero.com/eero-plus" target="_blank">eero Plus</a> subscription'),
        button: '$20/mo'
    }
};
/**
 * MyBundle streaming promo (type MYBUNDLE — auto-included, surfaces on the
 * Details cart promo lines) — the dev catalog carries per-tier SKUs
 * (`MyBundleTV-2026-2MO` / `…-3MO` / `…-4MO-FIXED`), so the month count is
 * parsed from the SKU; unparseable titles fall back to count-neutral copy.
 * `button: ''` blanks the old "$0 / Month" label (price-0 fallback renders
 * `Free` if a consumer ever needs a label). No `description` key on purpose:
 * a value containing PROMO/FREEMO/… would flip the Details step's
 * "first month $0" display.
 */
function myBundleOverride(title) {
    const months = title.match(/(\d+)\s*-?\s*MO/i);
    const pitch = months
        ? `<b>${months[1]} months</b> of streaming credit with MyBundle — pick the services you already love.`
        : `Streaming credit with MyBundle — pick the services you already love.`;
    return {
        title: 'Free streaming',
        bottomText: pitch,
        button: ''
    };
}
/**
 * Stamp the hardcoded new-design flavor over a UGPS response, in place.
 * Idempotent — PlanStep calls it once per buildOffers (the single ingest point
 * for both the standard and customerHub flows), before any templating, so
 * ExtrasStep (trio price buttons, addon rows) and DetailsStep (cart/summary
 * lines) all read the overridden values.
 */
function applyFlavorOverrides(ugpsData) {
    if (!ugpsData) {
        return;
    }
    // page-level plan-head (ignores the incoming value — old-design voice)
    ugpsData.flavor = { ...(ugpsData.flavor ?? {}), offersHeader: OFFERS_HEADER };
    for (const offer of ugpsData.offers ?? []) {
        const planOverride = PLAN_OVERRIDES[offer.title];
        if (planOverride && offer.flavor) {
            Object.assign(offer.flavor, planOverride, streamingFlavor(offer));
        }
        for (const addon of offer.addons ?? []) {
            if (!addon.flavor) {
                continue;
            }
            if (/^MyBundle/i.test(addon.title)) {
                Object.assign(addon.flavor, myBundleOverride(addon.title));
                continue;
            }
            const addonOverride = ADDON_OVERRIDES[addon.title];
            if (addonOverride) {
                Object.assign(addon.flavor, addonOverride);
            }
        }
    }
}
